import React from 'react';
import { Field, SearchSelect, TimeParts } from './controls';
import {
  durationHours, endMinutes, fmtDatePill, fmtTime, fromMinutes, toMinutes,
} from '../state/format';
import {
  crosses, inRange, interviewHolds, isOutsideWindow, isPending, isTimingSet,
  sameSideOverlap, shotPad, suggestedHours, takenRanges,
} from '../state/derive';
import {
  KIND_CHIP, MAX_EVENT_MINUTES, PACKAGE, RITUAL, SIDE, SIDE_LABEL,
  SMALL_HOURS_END, STEP_MINUTES, TIMING_SIDE_CHIP,
} from '../../constants';

const e = React.createElement;

const gridTimes = (extra) => {
  const out = [];
  for (let m = 0; m < 1440; m += STEP_MINUTES) out.push(m);
  extra.forEach((t) => {
    const m = toMinutes(t);
    if (m !== null && !Number.isNaN(m) && out.indexOf(m) < 0) out.push(m);
  });
  return out.sort((a, b) => a - b);
};

const sideWord = (side) => (SIDE_LABEL[side] || SIDE_LABEL[SIDE.BOTH]).toLowerCase();

/**
 * A pasted map link, as the server will take it.
 *
 * A phone's share sheet often hands over the address with no scheme, which
 * is still a link and not something to refuse. Anything carrying a space is
 * left exactly as typed, because that is prose and saying so is the field's
 * job rather than this one's.
 */
export const mapLinkTidy = (value) => {
  const text = String(value || '').trim();
  if (!text || /\s/.test(text)) return text;
  if (/^https?:\/\//i.test(text)) return text;
  // Only where it reads like a host, so a word does not become a link by
  // having https put in front of it.
  return /^[\w-]+(\.[\w-]+)+(\/|$|\?)/.test(text) ? `https://${text}` : text;
};

/** Whether what is in the field is an address a browser can open. */
export const mapLinkBad = (value) => {
  const text = mapLinkTidy(value);
  if (!text) return false;
  try {
    const url = new URL(text);
    return !/^https?:$/.test(url.protocol) || !url.hostname.includes('.');
  } catch (err) {
    return true;
  }
};

/**
 * One event in the timing phase. The couple works through the day a card at a
 * time: the card below waits until this one is settled, and a settled card
 * reads rather than edits. Every field here saves as it is answered; the
 * approval request goes once, when the form is submitted.
 */
const TimingCard = ({
  entry, position, events, windows, locked, error, venueError, linkServerError,
  venues, interviews, onChange, stepLocked, confirmed, editing, onConfirm,
  onReopen,
}) => {
  const held = !!stepLocked;
  // The form's own lock, before this card's place in the walk.
  const shutByForm = !!locked;
  const shut = shutByForm || held || !!confirmed;
  const pkg = entry.kind !== RITUAL;
  const suggested = suggestedHours(entry);
  const entered = durationHours(entry.start, entry.end);
  const outside = isOutsideWindow(entry, windows);
  // The catalogue, plus whatever this card already holds. A venue sold
  // outside the booking's own cities is not on the list, and dropping it
  // would quietly empty a card the couple never touched.
  const venueOptions = (() => {
    // This event's own city. One list is sent for the whole booking, so a
    // card on a day in another city would otherwise offer venues nobody
    // can get to from it.
    const here = (venues || []).filter(
      (v) => !v.city_id || !entry.city_id || v.city_id === entry.city_id
    );
    // Where a venue is only earns a line when it tells two of one name
    // apart. On a name nobody shares it is a place the couple never
    // mentioned, shown back to them as though they had.
    const seen = {};
    here.forEach((v) => {
      seen[v.name] = (seen[v.name] || 0) + 1;
    });
    const rows = here.map((v) => ({
      value: v.id,
      label: v.name,
      hint: seen[v.name] > 1
        ? [v.locality, v.city].filter(Boolean).join(', ')
        : '',
    }));
    if (entry.venue_id
        && !rows.some((o) => String(o.value) === String(entry.venue_id))) {
      rows.unshift({ value: entry.venue_id, label: entry.venue || 'Venue' });
    }
    return rows;
  })();
  // The city those options come from, which is this event's own.
  const venueCities = entry.city || '';
  // Named with the city the list is drawn from, so a couple who cannot
  // find their venue can see it is being offered the wrong city's rather
  // than that we have never heard of it. On the settled card too: it is
  // the same question, just answered already.
  const venueLabel = venueCities ? `Venue (${venueCities})` : 'Venue';
  // What the couple has asked for and ops has not approved.
  const asked = entry.pending || {};
  const pending = isPending(entry);
  const clash = sameSideOverlap(events, entry);

  // Guidance, never a rule: the entered duration beside what this kind of
  // event usually runs.
  let guide;
  if (suggested && entered !== null) {
    guide = `Duration ${entered} hrs · most ${(entry.name || 'such').toLowerCase()} events run about ${suggested} hrs`;
  } else if (suggested) {
    guide = `Most ${(entry.name || 'such').toLowerCase()} events run about ${suggested} hrs, a guide and not a rule.`;
  } else if (entered !== null) {
    guide = `Duration ${entered} hrs`;
  } else {
    guide = 'Set both times to see the duration.';
  }

  const askedLabel = (key) => {
    const value = asked[key];
    if (value === undefined || value === null || value === '') return null;
    if (key === 'date') return fmtDatePill(value);
    if (key === 'start' || key === 'end') return fmtTime(value);
    return String(value);
  };

  // Only once the form is in: before that every answer is a draft, so
  // saying so under each field tells the couple nothing. After it, ops has
  // the batch and this is the one thing they cannot see for themselves.
  const waiting = (key) => {
    if (!shutByForm) return null;
    const label = askedLabel(key);
    return label
      ? e('p', { className: 'wd-waiting', key: `w-${key}` }, `Waiting on Eventgraphia: ${label}`)
      : null;
  };

  // What ops turned back. The answer stays on the card and stays readable,
  // marked as refused with the reason ops gave, so the couple can see what
  // they asked for and what to do about it.
  const refused = entry.rejected || {};

  // What ops let through, with the note they left on it. Shown for the
  // same reason a refusal is: the couple asked, this is the answer.
  const allowed = entry.approved || {};

  const approvedNote = (key) => {
    const cell = allowed[key];
    if (!cell || !cell.reason) return null;
    return e('p', { className: 'wd-approved-note', key: `a-${key}` }, [
      e('strong', { key: 't' }, 'Approved'),
      e('span', { key: 'w' }, ` \u00b7 ${cell.reason}`),
    ]);
  };

  const rejectedNote = (key) => {
    const cell = refused[key];
    if (!cell) return null;
    return e('p', { className: 'wd-rejected-note', key: `r-${key}` }, [
      e('strong', { key: 't' }, 'Rejected'),
      cell.reason
        ? e('span', { key: 'w' }, ` \u00b7 ${cell.reason}`)
        : e('span', { key: 'w' }, ' \u00b7 ops has not said why yet'),
    ]);
  };

  const cell = (key, control) =>
    e('div', { className: `wd-cell${refused[key] ? ' is-rejected' : ''}`, key }, [
      refused[key]
        ? e('div', { className: 'wd-refused', key: 'refused' }, control)
        : control,
      waiting(key),
      rejectedNote(key),
      approvedNote(key),
    ]);

  // What the rest of this side's day already holds. The picker leaves those
  // slots unpickable rather than letting the couple choose one and be refused.
  // The sittings this side has booked on this day hold their minutes too.
  // The crew cannot be filming an event and sitting someone down at the
  // same time, so those minutes are no more free than another event's.
  const taken = takenRanges(events, entry)
    .concat(interviewHolds(interviews, entry.date, entry.side));
  // This event's own couple shot holds the minutes beside it, so a start
  // half an hour after the event before it is not free when a thirty minute
  // shot runs first.
  const pad = shotPad(entry);
  const startMin = toMinutes(entry.start);
  // A slot the day already holds is not offered at all: the couple picks
  // from what is left rather than from a list of refusals.
  // The booking opens and closes on the package's own hours. The first
  // event can be pushed back inside them and the last pulled forward, so
  // the two readings are offered as a floor and a ceiling rather than as
  // fixed answers the couple cannot touch.
  const floor = toMinutes(entry.package_start);
  // The close holds for every event on the day, an added ritual included,
  // not only the one that carries it. The events run one after another, so
  // one in the middle ending past the close leaves those below it nothing.
  // A day sold with only some of its hours has no window row, but its first
  // and last events still carry the opening and the close, so those stand
  // in for it.
  const dayWindow = (windows || {})[entry.date] || null;
  const onDay = (events || []).filter((x) => x.date === entry.date);
  const dayFirst = onDay.find((x) => x.package_start);
  const dayLast = onDay.find((x) => x.package_end);
  const closeAt = entry.package_end || (dayWindow && dayWindow.end)
    || (dayLast && dayLast.package_end) || '';
  const openAt = (dayWindow && dayWindow.start)
    || (dayFirst && dayFirst.package_start) || '';
  // Without an opening hour the day's earliest event is where it starts
  // reading, as dayOpen does below, so the close still caps the starts the
  // end list would have nothing left after.
  // On a day that closes in the small hours, a start in them belongs to
  // the night's tail, not the day's front, so it is passed over while an
  // evening start is there to open the day instead. Only then: on a day
  // that closes before midnight, a start left past the close (ops moved the
  // close after it was saved) would otherwise open the day on it.
  const dayStarts = onDay.map((x) => toMinutes(x.start))
    .filter((m) => m !== null && !Number.isNaN(m));
  const closeMin = closeAt ? toMinutes(closeAt) : null;
  const overnight = closeMin !== null && !Number.isNaN(closeMin)
    && closeMin <= SMALL_HOURS_END;
  const evening = overnight
    ? dayStarts.filter((m) => m >= SMALL_HOURS_END) : dayStarts;
  const pool = evening.length ? evening : dayStarts;
  const earliestStart = pool.length ? Math.min.apply(null, pool) : null;
  const dayStart = openAt ? toMinutes(openAt) : earliestStart;
  const dayClose = dayStart !== null && closeAt
    ? endMinutes(fromMinutes(dayStart), closeAt) : null;
  // A start past the close is refused the same way. Read along the day's
  // hours, so 1am on a day that runs to 2am still sits inside them. A time
  // before the day opens is the next morning otherwise, which is past the
  // close too: on a day that ends before midnight it is not a slot at all.
  const beforeClose = (m) => {
    if (dayStart === null || dayClose === null
      || Number.isNaN(dayStart) || Number.isNaN(dayClose)) return true;
    if (m >= dayStart) return m < dayClose;
    return dayClose > 1440 && m + 1440 < dayClose;
  };
  // A package that closes after midnight carries the small hours of the next
  // morning, so a start before it opens is offered when it falls inside that
  // tail rather than being read as the morning before coverage began.
  // The day's own close where it is known, so a card that carries neither
  // end of the package still reads a 00:30 below it as tonight's tail.
  const closes = dayClose !== null && !Number.isNaN(dayClose) ? dayClose
    : endMinutes(entry.package_start, entry.package_end);
  const opened = (m) => {
    if (floor === null || Number.isNaN(floor)) return true;
    if (m >= floor) return true;
    return closes > 1440 && m + 1440 <= closes;
  };
  // Where this day starts reading, for deciding whether a small hours
  // reading is this morning or the next. The package says so where it
  // carries hours; where it does not, the day's own earliest event does,
  // and without either there is nothing to roll against.
  const dayOpen = floor !== null && !Number.isNaN(floor)
    ? floor : earliestStart;
  // Read along the package rather than up the clock, so a day that runs to
  // 2am ends on its small hours instead of opening on them.
  const alongDay = (m) => (dayOpen !== null && m < dayOpen ? m + 1440 : m);
  const alongEnd = (m) => {
    const from = startMin === null || Number.isNaN(startMin) ? floor : startMin;
    if (from === null || Number.isNaN(from)) return m;
    return m < from ? m + 1440 : m;
  };
  // The day runs forward: an event placed after another cannot start before
  // that one has finished. A shot hanging off the end of the event before is
  // already inside its range, and one hanging off the front of this event is
  // what `pad.before` holds back here.
  const order = (events || []).map((x) => String(x.id));
  const mine = order.indexOf(String(entry.id));
  const earliest = taken.reduce((top, range) => {
    const at = order.indexOf(String(range.id));
    if (at < 0 || at >= mine) return top;
    return top === null || range.end > top ? range.end : top;
  }, null);
  // And the other way: an event placed before another has to be over, its
  // own shot included, before that one starts. Without this a ritual moved
  // above a package event offered the whole day to start in, then no end
  // that did not run into the event below it.
  // Only a start inside the package's small hours tail is the next
  // morning. One before the package opens is this morning: the getting
  // ready and the sittings held ahead of the first event sit there, and
  // reading them as tomorrow left only the small hours to pick from.
  const tailRoll = (m) => (closes > 1440 && m + 1440 <= closes ? m + 1440 : m);
  const nextUp = taken.reduce((low, range) => {
    const at = order.indexOf(String(range.id));
    if (at < 0 || at <= mine) return low;
    return low === null || tailRoll(range.start) < tailRoll(low.start)
      ? range : low;
  }, null);
  const latest = nextUp ? tailRoll(nextUp.start) : null;
  // Everything but the event below, kept apart so the card can tell when
  // that event is the one leaving no room rather than the one above it.
  const freeStarts = gridTimes([entry.start])
    .filter(opened)
    .filter(beforeClose)
    // Read along the day, not up the clock: the event before this one
    // can finish after midnight, and its end is held as the rolled
    // reading. Comparing a raw 6am against that put the whole morning
    // behind an event that had already finished.
    .filter((m) => earliest === null || alongDay(m) - pad.before >= earliest)
    .filter((m) => !crosses(m - pad.before, m, taken) && !inRange(m, taken));
  const fitStarts = freeStarts
    // Read the same way as the event below, so a morning ahead of an
    // event that opens the day stays this morning rather than tomorrow.
    .filter((m) => latest === null || tailRoll(m) + pad.after < latest);
  // Only then is moving this card below that event an answer. Squeezed by
  // the event above instead, moving it down still leaves nowhere to go.
  const shutByNext = !!nextUp && freeStarts.length > 0 && !fitStarts.length;
  const startOptions = [{ value: '', label: 'Select a time' }].concat(
    fitStarts
      .sort((a, b) => alongDay(a) - alongDay(b))
      .map((m) => ({ value: fromMinutes(m), label: fmtTime(fromMinutes(m)) }))
  );
  const endOptions = [{ value: '', label: 'Select a time' }].concat(
    gridTimes([entry.end])
      .filter((m) => {
        if (closeAt) {
          // Measured from the event's own start, because a booking that
          // closes after midnight reads as the smaller number.
          const shut = endMinutes(entry.start, closeAt);
          if (endMinutes(entry.start, fromMinutes(m)) > shut) return false;
        }
        if (startMin === null || Number.isNaN(startMin)) return true;
        const end = endMinutes(entry.start, fromMinutes(m));
        // An end before the start is the same event running overnight, which
        // endMinutes has already rolled; only a crossing is refused.
        if (end <= startMin) return false;
        // One event, not a day of them. Without this the list runs the whole
        // way round the clock and an end picked just before the start reads
        // as an event lasting nearly a day.
        if (end - startMin > MAX_EVENT_MINUTES) return false;
        return !crosses(startMin - pad.before, end + pad.after, taken);
      })
      // Read forward from the event's own start, so an end just before
      // midnight is offered ahead of midnight itself rather than behind it.
      .sort((a, b) => alongEnd(a) - alongEnd(b))
      .map((m) => ({ value: fromMinutes(m), label: fmtTime(fromMinutes(m)) }))
  );
  // Offered only where the list above would offer the same end. Written
  // straight to the card, it skips every check the list makes: the package
  // close, the longest an event runs, and the events already on the day.
  const suggestedEnd = suggested && entry.start
    ? fromMinutes(toMinutes(entry.start) + suggested * 60)
    : null;
  const canSuggest = Boolean(suggestedEnd)
    && endOptions.some((o) => o.value === suggestedEnd);

  // A card that is not this one's turn reads its answers instead of offering
  // fields the couple cannot use.
  const readOnlyField = (label, value, empty, caption) =>
    e('div', { className: 'wd-fieldwrap', key: 'ro' }, [
      e('span', { className: 'wd-flabel', key: 'l' }, label),
      e('div', { className: 'wd-timelocked', key: 'v', title: value || undefined }, value || empty),
      caption ? e('span', { className: 'wd-caption', key: 'c' }, caption) : null,
    ]);

  // The step that closes the card, and the card below it waits on. It needs
  // both times before it means anything.
  const isOpenCard = !held && !confirmed && !shutByForm;
  // An event card carries the whole event, so it is answered whole: the walk
  // does not move on to the next one with a field on this one still empty.
  const NEEDED = [
    ['start', 'start time'],
    ['end', 'end time'],
    ['venue', 'venue'],
    ['map_link', 'maps link'],
  ];
  const missing = NEEDED.filter(
    ([key]) => !String(entry[key] || '').trim()
  ).map(([, label]) => label);
  // A shot taken inside this event is part of the event, so the card is not
  // answered until it has been placed.
  const shotInside = entry.couple_shot && entry.couple_shot.position === 'during';
  if (shotInside && !entry.couple_shot.start) missing.push('couple shot time');
  // A disabled button says nothing about why it is disabled, and the one
  // thing people miss is the couple shot, which sits on a card of its own
  // below this one. So the button stays live and answers when it is used.
  const [tried, setTried] = React.useState(false);
  // A save is on its way. The card closes optimistically, so this only
  // stands where the server is still being waited on, which is where a
  // second click used to land and ask for the same thing again.
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => {
    // Done, or refused and handed back: either way the button is free.
    if (confirmed || error) setSaving(false);
  }, [confirmed, error]);
  // Said as soon as it reads as words rather than waiting for the server to
  // refuse it, since the couple is looking at the field when they paste.
  const linkError = mapLinkBad(entry.map_link)
    ? 'Paste the link itself, not the name of the place'
    : '';
  const why = tried && missing.length > 0;

  return e('div', {
    'data-wd-timing': String(entry.id),
    'aria-disabled': held ? 'true' : undefined,
    className: `wd-trow${outside || pending ? ' is-outside' : ''}${held ? ' is-held' : ''}${
      confirmed ? ' is-confirmed' : ''}${
      Object.keys(entry.rejected || {}).length ? ' has-rejected' : ''}`,
  }, [
    e('div', { className: 'wd-top', key: 'top' }, [
      e('span', { className: 'wd-num', key: 'n' }, String(position + 1)),
      e('span', { className: 'wd-nm', key: 'nm' }, entry.name || 'Unnamed ritual'),
      e(
        'span',
        { className: `wd-badge ${pkg ? 'is-pkg' : 'is-ritual'}`, key: 'b' },
        KIND_CHIP[pkg ? PACKAGE : RITUAL]
      ),
      confirmed && !shutByForm
        ? e(
            'button',
            {
              key: 'pencil',
              type: 'button',
              className: 'wd-pencil',
              'aria-label': `Edit ${entry.name || 'this event'}`,
              title: 'Edit this event',
              onClick: onReopen,
            },
            '✎'
          )
        : null,
      e(
        'span',
        { className: `wd-sidetag is-${TIMING_SIDE_CHIP[entry.side] ? entry.side : SIDE.BOTH}`, key: 's' },
        TIMING_SIDE_CHIP[entry.side] || TIMING_SIDE_CHIP.both
      ),
    ]),
    // Whose event this is reverts to what the estimation sold when ops
    // turns the ask back, so the chip above already reads the right side.
    // What was asked for and why it was refused is said here instead, or
    // the couple is left wondering where their change went.
    refused.side
      ? e('p', { className: 'wd-rejected-note', key: 'r-side' }, [
          e('strong', { key: 't' }, 'Rejected'),
          e('span', { key: 'w' },
            ` \u00b7 you asked for ${sideWord(refused.side.value)}`
            + (refused.side.reason ? ` \u00b7 ${refused.side.reason}` : '')),
        ])
      : null,
    // A ritual ops turned back stays on the day, so the couple can change
    // it or take it off. The card says why rather than leaving them to guess.
    refused.added_ritual
      ? e('p', { className: 'wd-rejected-note', key: 'r-ritual' }, [
          e('strong', { key: 't' }, 'Rejected'),
          e('span', { key: 'w' },
            refused.added_ritual.reason
              ? ` \u00b7 ${refused.added_ritual.reason}`
              : ' \u00b7 ops has not said why yet'),
        ])
      : null,
    // Who we are sending. Only the payload knows, and it sends this to the
    // crew rather than to the couple, so an empty list simply draws nothing.
    (entry.crew || []).length
      ? e('p', { className: 'wd-crew', key: 'crew' }, [
          e('span', { className: 'wd-crew-tag', key: 't' }, 'ASSIGNED'),
          e(
            'span',
            { key: 'n' },
            entry.crew
              .map((c) => (c.role ? `${c.name} (${c.role})` : c.name))
              .join(', ')
          ),
        ])
      : null,
    // How we run this kind of event. Ours, like the crew line above it, so
    // the payload only carries it to the crew and the couple sees nothing.
    entry.management && (entry.management.dress_code || entry.management.notes)
      ? e('div', { className: 'wd-mgmt', key: 'mgmt' }, [
          entry.management.dress_code
            ? e('p', { className: 'wd-mgmt-row', key: 'd' }, [
                e('span', { className: 'wd-mgmt-tag', key: 't' }, 'DRESS CODE'),
                e('span', { key: 'v' }, entry.management.dress_code),
              ])
            : null,
          entry.management.notes
            ? e('p', { className: 'wd-mgmt-row', key: 'n' }, [
                e('span', { className: 'wd-mgmt-tag', key: 't' }, 'NOTES'),
                e('span', { key: 'v' }, entry.management.notes),
              ])
            : null,
        ])
      : null,
    // Why the card is closed, said before the fields it closes.
    held
      ? e('p', { className: 'wd-held', key: 'held' }, 'Set the times for the event above first.')
      : null,
    e('div', { className: 'wd-times', key: 'times' }, [
      cell('start', shut
        ? readOnlyField('Start time', entry.start && fmtTime(entry.start), 'Not set')
        : e(TimeParts, {
            key: 'start',
            label: 'Start time',
            value: entry.start || '',
            options: startOptions,
            // Placed ahead of an event that leaves no room in front of it,
            // so the order is what has to change, not the clock.
            caption: shutByNext
              ? `Nothing is free before ${nextUp.name || 'the next event'}. Move this event below it to pick a time.`
              : entry.package_start
                ? `Your package starts at ${fmtTime(entry.package_start)}. You can start later, not earlier.`
                : undefined,
            onChange: (v) => onChange({ start: v }),
          })),
      cell('end', shut
        ? readOnlyField('End time', entry.end && fmtTime(entry.end), 'Not set')
        : e(TimeParts, {
            key: 'end',
            label: 'End time',
            value: entry.end || '',
            options: endOptions,
            // The end is read forward from the start, so there is nothing
            // to pick it against until the start is set.
            disabled: !entry.start,
            caption: !entry.start
              ? 'Set the start time first.'
              : closeAt
                ? `Your package ends at ${fmtTime(closeAt)}. You can end earlier, not later.`
                : undefined,
            onChange: (v) => onChange({ end: v }),
          })),
      e('div', { className: 'wd-guide', key: 'guide' }, [
        e('span', { key: 't' }, guide),
        canSuggest && !entry.end && !shut
          ? e(
              'button',
              {
                key: 'chip',
                type: 'button',
                className: 'wd-chip',
                onClick: () => onChange({ end: suggestedEnd }),
              },
              `Use ${suggested} hrs`
            )
          : null,
      ]),
    ]),
    // Why some of the day is unpickable, named rather than left to guesswork.
    !shut && taken.length
      ? e(
          'div',
          { className: 'wd-taken', key: 'taken' },
          taken.map((r) =>
            e(
              'p',
              { key: String(r.id) },
              `${fmtTime(fromMinutes(((r.start % 1440) + 1440) % 1440))} to ${fmtTime(fromMinutes(r.end % 1440))} is taken by ${r.name} (${sideWord(r.side)})${r.shot ? ', couple shot included' : ''}`
            )
          )
        )
      : null,
    // Different side overlaps are normal, so only the same side is warned about.
    clash
      ? e(
          'div',
          { className: 'wd-warnbox', key: 'clash' },
          `This overlaps ${clash.name} on the same side, ${fmtTime(clash.start)} to ${fmtTime(clash.end)}. Two events on one side cannot run at once.`
        )
      : null,
    outside && isTimingSet(entry)
      ? e(
          'div',
          { className: 'wd-warnbox', key: 'outside' },
          'Outside your package hours for this day, so this needs coordinator approval.'
        )
      : null,
    e('div', { className: 'wd-venue', key: 'where' }, [
      cell('venue', shut
        ? readOnlyField(venueLabel, entry.venue, 'Not set')
        : e(SearchSelect, {
            key: 'venue',
            label: venueLabel,
            value: entry.venue_id || '',
            options: venueOptions,
            placeholder: 'Where does this happen?',
            error: venueError || null,
            // What they wrote, where the list had nothing for it. Held on
            // the card as text until it is approved, which is when it
            // becomes a venue of its own.
            fallbackText: entry.venue || '',
            listNote: entry.city
              ? `Venues in ${entry.city}. Not here? Type yours.`
              : 'Not here? Type yours.',
            caption: !entry.venue_id && entry.venue
              ? 'Not on our list, so your coordinator will confirm it.'
              : undefined,
            // A venue we have never heard of is still their venue, so it
            // is taken as written rather than leaving the card unanswerable.
            onCreate: (text) => onChange({ venue_id: null, venue: text }),
            createLabel: 'We will use',
            // Both, because the catalogue row is what the crew is sent to
            // and its name is what every reader of the card prints. The
            // name alone, not the line the list reads by: the locality and
            // the city are there to tell two venues apart on screen.
            onChange: (id) => onChange({
              venue_id: id || null,
              venue: ((venues || []).find(
                (v) => String(v.id) === String(id)
              ) || {}).name || '',
            }),
          })),
      cell('map_link', shut
        ? readOnlyField('Google maps link', entry.map_link, 'Not set')
        : e(Field, {
            key: 'map',
            label: 'Google maps link',
            // A link, not a description. The crew taps this to be driven
            // there, so the name of the place is no use to them.
            type: 'url',
            value: entry.map_link || '',
            placeholder: 'Paste the link to where this happens',
            onChange: (v) => onChange({ map_link: v }),
            // A link pasted off a phone's share sheet often arrives with
            // no scheme, which is still a link. One is put in front of it
            // rather than refusing what they pasted.
            onBlur: () => {
              const tidy = mapLinkTidy(entry.map_link || '');
              if (tidy !== (entry.map_link || '')) {
                onChange({ map_link: tidy });
              }
            },
            // What the card can tell on its own first, then the server's
            // word on what was sent.
            error: linkError || linkServerError || null,
          })),
    ]),
    // A refusal about the card as a whole, its times or whether it can
    // close, said once above the button rather than under one field.
    error
      ? e('p', { className: 'wd-fielderror', key: 'err', role: 'alert' }, error)
      : null,
    // The card has no field for notes, so a note awaiting review is said here.
    waiting('notes'),
    isOpenCard
      ? e(
          'button',
          {
            key: 'confirm',
            type: 'button',
            className: `wd-btn wd-btn-brand wd-confirm${
              missing.length || linkError ? ' is-waiting' : ''}`,
            'aria-disabled': (missing.length || linkError || saving)
              ? 'true' : undefined,
            // A link that is really words is not a field left empty, so it
            // stops the card closing on its own account. A click while the
            // last one is still out does nothing: it only ever asked the
            // server the same thing twice.
            onClick: () => {
              if (saving) return;
              if (missing.length || linkError) {
                setTried(true);
                return;
              }
              setSaving(true);
              onConfirm();
            },
          },
          // Reopened from the pencil, so it is an edit being saved rather
          // than an answer being given for the first time. A save still on
          // its way says so, because a button that reads the same either
          // way looks as though the click did nothing.
          saving ? 'Saving...' : (editing ? 'Save' : 'Everything looks good')
        )
      : null,
    isOpenCard && missing.length
      ? e(
          'p',
          {
            className: `wd-caption${why ? ' wd-needs' : ''}`,
            key: 'needtimes',
            role: why ? 'alert' : undefined,
          },
          why
            ? `This event is not settled yet. Still to fill: ${
              missing.join(', ')}.`
            : `Still to fill: ${missing.join(', ')}.`
        )
      : null,

  ]);
};

export default TimingCard;
